import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const PRIORITY_COLORS = {
  Critical: '#dc2626', Highest: '#dc2626',
  High: '#d97706',
  Medium: '#f59e0b',
  Low: '#16a34a', Lowest: '#6b7280',
};

function getColor(p) { return PRIORITY_COLORS[p] || '#94a3b8'; }

export default function WorkloadPage({ api, uploadId, teamOnly }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState('tickets');
  const [sortDir, setSortDir] = useState('desc');
  const toggleDir = () => setSortDir(d => d === 'desc' ? 'asc' : 'desc');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!uploadId) { setData([]); return; }
    setLoading(true);
    fetch(`${api}/api/uploads/${uploadId}/workload?team_only=${!!teamOnly}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [uploadId, api, teamOnly]);

  if (!uploadId) return <div className="empty"><p>Select an export to view assignee workload.</p></div>;
  if (loading) return <div className="loading">Loading workload…</div>;

  let filtered = search
    ? data.filter(d => d.assignee.toLowerCase().includes(search.toLowerCase()))
    : data;

  const dir = sortDir === 'desc' ? -1 : 1;
  if (sortBy === 'tickets') filtered = [...filtered].sort((a, b) => (b.ticket_count - a.ticket_count) * dir);
  if (sortBy === 'time') filtered = [...filtered].sort((a, b) => (b.time_spent_seconds - a.time_spent_seconds) * dir);
  if (sortBy === 'age') filtered = [...filtered].sort((a, b) => (b.avg_age_days - a.avg_age_days) * dir);

  const unassigned = filtered.find(d => d.assignee === 'Unassigned');
  const assigned = filtered.filter(d => d.assignee !== 'Unassigned');

  const maxTickets = Math.max(...filtered.map(d => d.ticket_count), 1);

  // Chart data — top 10 by ticket count
  const chartData = [...data]
    .sort((a, b) => b.ticket_count - a.ticket_count)
    .slice(0, 10)
    .map(d => {
      const out = { name: d.assignee.split(' ')[0] };
      Object.entries(d.priorities).forEach(([p, v]) => { out[p] = v; });
      return out;
    });

  const allPriorities = [...new Set(data.flatMap(d => Object.keys(d.priorities)))];

  return (
    <div>
      <div className="grid-3" style={{ marginBottom: 24 }}>
        <div className="card">
          <div className="card-title">Assignees</div>
          <div className="card-value">{data.filter(d => d.assignee !== 'Unassigned').length}</div>
          <div className="card-sub">People with open tickets</div>
        </div>
        <div className="card">
          <div className="card-title">Busiest</div>
          <div className="card-value">{data[0]?.assignee?.split(' ')[0] ?? '—'}</div>
          <div className="card-sub">{data[0]?.ticket_count} open tickets</div>
        </div>
        <div className="card">
          <div className="card-title">Unassigned</div>
          <div className="card-value" style={{ color: unassigned ? 'var(--warn)' : undefined }}>{unassigned?.ticket_count ?? 0}</div>
          <div className="card-sub">Tickets with no owner</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="section-title">Priority Distribution — Top 10 Assignees</div>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData} margin={{ left: 0, right: 16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
            {allPriorities.map(p => <Bar key={p} dataKey={p} stackId="a" fill={getColor(p)} />)}
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div className="section-title" style={{ margin: 0 }}>All Assignees</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              placeholder="Search name…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ fontFamily: 'DM Sans', fontSize: 13, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 7, outline: 'none', background: 'var(--surface-2)' }}
            />
            <select className="upload-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
              <option value="tickets">Sort: Ticket count</option>
              <option value="time">Sort: Time spent</option>
              <option value="age">Sort: Avg age</option>
            </select>
            <button onClick={toggleDir} title={sortDir === 'desc' ? 'Descending' : 'Ascending'}
              style={{ padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 7, background: 'var(--surface)', cursor: 'pointer', fontSize: 14, lineHeight: 1, color: 'var(--accent)', fontWeight: 700 }}>
              {sortDir === 'desc' ? '↓' : '↑'}
            </button>
          </div>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Assignee</th>
                <th>Tickets</th>
                <th>Ticket load</th>
                <th>Time Logged</th>
                <th>Avg Age</th>
                <th>Priorities</th>
                <th>Projects</th>
              </tr>
            </thead>
            <tbody>
              {assigned.map(person => {
                const critCount = (person.priorities['Critical'] || 0) + (person.priorities['Highest'] || 0) + (person.priorities['High'] || 0);
                return (
                  <tr key={person.assignee}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--accent-light)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12, flexShrink: 0 }}>
                          {person.assignee.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontWeight: 500, fontSize: 13 }}>{person.assignee}</div>
                          {critCount > 0 && <div style={{ fontSize: 11, color: 'var(--critical)' }}>⚠ {critCount} critical/high</div>}
                        </div>
                      </div>
                    </td>
                    <td style={{ fontFamily: 'DM Mono', fontWeight: 600 }}>{person.ticket_count}</td>
                    <td style={{ minWidth: 120 }}>
                      <div className="bar-bg">
                        <div className="bar-fill" style={{ width: `${(person.ticket_count / maxTickets) * 100}%` }} />
                      </div>
                    </td>
                    <td style={{ fontFamily: 'DM Mono', fontSize: 12 }}>{person.time_spent || '—'}</td>
                    <td style={{ fontFamily: 'DM Mono', fontSize: 12 }}>{person.avg_age_days}d</td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                        {Object.entries(person.priorities).map(([p, c]) => (
                          <span key={p} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, padding: '1px 6px', borderRadius: 100, background: getColor(p) + '22', color: getColor(p), fontWeight: 600 }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: getColor(p), display: 'inline-block' }} />{c} {p}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      {person.projects.map(p => <span key={p} className="pill">{p}</span>)}
                    </td>
                  </tr>
                );
              })}
              {unassigned && (
                <tr style={{ background: 'var(--warn-light)' }}>
                  <td><span style={{ fontWeight: 500, color: 'var(--warn)' }}>Unassigned</span></td>
                  <td style={{ fontFamily: 'DM Mono', fontWeight: 600, color: 'var(--warn)' }}>{unassigned.ticket_count}</td>
                  <td>
                    <div className="bar-bg">
                      <div className="bar-fill" style={{ width: `${(unassigned.ticket_count / maxTickets) * 100}%`, background: 'var(--warn)' }} />
                    </div>
                  </td>
                  <td style={{ fontFamily: 'DM Mono', fontSize: 12 }}>{unassigned.time_spent || '—'}</td>
                  <td style={{ fontFamily: 'DM Mono', fontSize: 12 }}>{unassigned.avg_age_days}d</td>
                  <td>
                    {Object.entries(unassigned.priorities).map(([p, c]) => (
                      <span key={p} style={{ fontSize: 11, padding: '1px 6px', borderRadius: 100, background: getColor(p) + '22', color: getColor(p), fontWeight: 600, marginRight: 3 }}>{c} {p}</span>
                    ))}
                  </td>
                  <td>{unassigned.projects.map(p => <span key={p} className="pill">{p}</span>)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
